import {gql} from '@apollo/client';
import React from 'react';

import isArchived from 'archive/isArchived';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {updateProcedureRunAnalysis} from 'procedures/network/ProcedureRunAnalysisDAO';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {EditAnalysisListQuery} from './__generated/EditAnalysisListQuery';
import {
  doEditableAnalysisSearch,
  editableAnalysisSearchUrl,
  editableAnalysisSearchFilters,
  editableAnalysisSearchSorts,
  EditableAnalysisSearchResponse,
} from './EditableAnalysisSearch';
import EditableSearchList, {EditableSearchItem} from './EditableSearchList';

export const EDIT_ANALYSIS_LIST_QUERY = gql`
  query EditAnalysisListQuery($procedureRunId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    procedureRun(id: $procedureRunId) {
      ... on ProcedureRunNode {
        id
        name
        analyses {
          ... on AnalysisNode {
            id
            name
            archiveRecord {
              purpose
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Show all Analyses linked to the procedure run, and let the user add/remove Analyses
 *
 * Note: the UX and styling choices match the EditableStudiesList component, but we could
 * potentially created a shared "EditableList" component in the future
 */
/**
 * Renders a list of editable analyses.
 *
 * @param procedureRunId - The ID of the procedure run.
 * @param renderWhileLoading - Optional. A function that renders content while the data is loading. Defaults to a centered horizontal throbber.
 * @returns The JSX element representing the editable analysis list.
 */
export default function EditableAnalysisList({
  procedureRunId,
  renderWhileLoading = () => <CenteredHorizontalThrobber />,
}: {
  procedureRunId: string;
  renderWhileLoading?: () => JSX.Element;
}): JSX.Element | null {
  const queryResult = useBenchlingQuery<EditAnalysisListQuery>(EDIT_ANALYSIS_LIST_QUERY, {
    variables: {procedureRunId},
    fetchPolicy: 'network-only',
  });

  if (queryResult.type === QueryResultType.ERROR) {
    // BenchlingQuery errors are caught and surfaced to the user via a Toast popup
    return null;
  }

  let isLoading: boolean = true;
  let selectedItems: Array<EditableSearchItem> = [];
  if (queryResult.type === QueryResultType.PENDING) {
    // Do nothing
  } else if (isInaccessible(queryResult.data.procedureRun)) {
    return <div>You do not have permission to view this experiment.</div>;
  } else {
    isLoading = false;
    selectedItems = queryResult.data.procedureRun.analyses
      .reduce((items, analysis) => {
        if (isInaccessible(analysis) || isArchived(analysis)) {
          return items;
        }
        return [
          ...items,
          {
            id: analysis.id,
            name: analysis.name,
          },
        ];
      }, [])
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  const onAnalysisItemSelected = (analysis: EditableSearchItem, isLinked: boolean): void => {
    updateProcedureRunAnalysis(procedureRunId, analysis.id, isLinked).then(() => {
      queryResult.refetch();
    });
  };

  return (
    <EditableSearchList<EditableAnalysisSearchResponse>
      selectedItems={selectedItems}
      onSelectItem={onAnalysisItemSelected}
      isLoading={isLoading}
      renderWhileLoading={renderWhileLoading}
      doSearch={doEditableAnalysisSearch}
      searchUrl={editableAnalysisSearchUrl}
      filters={editableAnalysisSearchFilters}
      sorts={editableAnalysisSearchSorts}
      placeholderSearchText="Search for analyses"
      emptyListMessage="This experiment has not been linked to any analyses."
    />
  );
}
